import Lenis from 'lenis';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'lenis/dist/lenis.css';
import 'simplebar/dist/simplebar.css';
import { initSubmv } from './submv.js';
import { initPaneSticky } from './pane-sticky.js';
import { initProcessScroll } from './process-scroll.js';
import { initProcessExit } from './process-exit.js';
import { startGradient as startMove } from './effects/animated-gradient-monterey.js';
import { initScrollbar } from './scrollbar.js';
import { initWorkScroll } from './work-scroll.js';
import { areaRevealSelector, initAreaReveal } from './area-reveal.js';
import { initBrandTone } from './brand-tone.js';
import { createRevealActivity } from './reveal-activity.js';
import { initWorkPreviews } from './work-preview.js';
import { initDialogs } from './dialogs.js';
import { initContactForm } from './contact-form.js';
import { initMoviePlayback } from './movie-playback.js';

const cleanups = [];
const revealActivity = createRevealActivity();
cleanups.push(() => revealActivity.destroy());
cleanups.push(revealActivity.subscribe((active) => document.body.classList.toggle('is-area-reveal-active', active)));
cleanups.push(() => document.body.classList.remove('is-area-reveal-active'));
const listen = (element, event, callback) => {
  element.addEventListener(event, callback);
  cleanups.push(() => element.removeEventListener(event, callback));
};
const desktop = window.matchMedia('(width >= 60rem)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
cleanups.push(initWorkPreviews(document.querySelectorAll('.work-preview'), reducedMotion));
const userAgent = navigator.userAgent;
const isTablet = /iPad|Tablet|PlayBook|Silk/i.test(userAgent)
  || (/Android/i.test(userAgent) && !/Mobile/i.test(userAgent))
  || (/Mac/i.test(navigator.platform || userAgent) && navigator.maxTouchPoints > 1);
const dialog = document.querySelector('#navigation');
const contactDialog = document.querySelector('#contact');
const savedScroll = import.meta.hot?.data.scrollPosition;
const initialScroll = window.scrollY;
const page = document.querySelector('.page');
const pageBar = initScrollbar(page, {
  axis: 'y', label: 'ページ本文',
  onInteract: () => lenis.scrollTo(lenis.actualScroll, { immediate: true }),
});
const lenis = new Lenis({
  wrapper: pageBar.scrollElement,
  content: pageBar.contentElement,
  eventsTarget: page,
  autoRaf: true,
  smoothWheel: !isTablet,
  lerp: .09,
  anchors: true,
  stopInertiaOnNavigate: true,
  prevent: () => dialog.open || contactDialog.open,
});
cleanups.push(initPaneSticky(document.querySelectorAll('.visual > .visual-main, .visual > .rail'), desktop));
cleanups.push(initAreaReveal(document.querySelectorAll(areaRevealSelector), {
  root: pageBar.scrollElement, reducedMotion, onActivityChange: (active) => revealActivity.setActive(active),
  onReveal: (area) => {
    if (area.matches('.mv')) area.querySelector('video')?.dispatchEvent(new Event('ffd:revealend'));
  },
}));
cleanups.push(startMove(document.querySelector('.rail-intro .reserve-gradient'), { scroller: pageBar.scrollElement, revealActivity }));

cleanups.push(initProcessScroll(document.querySelector('.approach'), lenis, reducedMotion, { scroller: pageBar.scrollElement, desktop }));
cleanups.push(initProcessExit(document.querySelector('[data-process-exit]'), document.querySelector('#works-title'), document.querySelector('.brand'), lenis, reducedMotion, { scroller: pageBar.scrollElement, followingSection: document.querySelector('#works') }));
cleanups.push(initWorkScroll(document.querySelector('.work-panes'), lenis, desktop, { scroller: pageBar.scrollElement }));
cleanups.push(initSubmv(document.querySelector('.submv'), reducedMotion, { desktop, scroller: pageBar.scrollElement, revealActivity, lenis }));
cleanups.push(initBrandTone(document.querySelector('.brand'), {
  scroller: pageBar.scrollElement, canvases: document.querySelectorAll('.submv .flyeye, .rail-intro canvas'), revealActivity,
}));

// 初期ハッシュとHMR前の縦位置を、新しいスクロール枠へ引き継ぐ。
const initialFrame = window.requestAnimationFrame(() => {
  const target = document.getElementById(location.hash.slice(1));
  lenis.scrollTo(savedScroll ?? target ?? initialScroll, { immediate: true });
});
cleanups.push(() => window.cancelAnimationFrame(initialFrame));

listen(document.querySelector('.brand'), 'click', (event) => {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  lenis.scrollTo('#top', { immediate: reducedMotion.matches });
  history.replaceState(null, '', '#top');
});

const dialogs = initDialogs([
  { dialog, buttons: document.querySelectorAll('.menu') },
  { dialog: contactDialog, buttons: document.querySelectorAll('[data-contact-open]') },
], {
  reducedMotion,
  onOpen: () => {
    lenis.stop();
    document.body.classList.add('menu-open');
  },
  onClose: () => {
    document.body.classList.remove('menu-open');
    lenis.start();
  },
});
cleanups.push(dialogs.destroy);
cleanups.push(initContactForm(contactDialog.querySelector('form'), { closeDialog: dialogs.close }));
dialog.querySelectorAll('nav a[href^="#"]').forEach((link) => {
  listen(link, 'click', (event) => {
    const target = document.querySelector(link.getAttribute('href'));
    if (!target) return;
    event.preventDefault();
    dialogs.closeOnClick(dialog, () => {
      lenis.scrollTo(target, { immediate: reducedMotion.matches });
      history.replaceState(null, '', link.getAttribute('href'));
    });
  });
});

const onDesktopChange = () => lenis.resize();
listen(desktop, 'change', onDesktopChange);

document.querySelectorAll('.mv, .rail-art').forEach((area) => {
  cleanups.push(initMoviePlayback(area, { reducedMotion, revealActivity }));
});

if (import.meta.hot) import.meta.hot.dispose((data) => {
  const position = lenis.scroll;
  data.scrollPosition = position;
  cleanups.forEach((cleanup) => cleanup());
  lenis.destroy();
  pageBar.destroy();
  window.scrollTo(0, position);
});
